import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  selectFavorites,
  selectScreeningStatus,
  selectScreeningProgress,
  selectAIAnalysis,
  selectChartData,
  selectGlobalMessage,
  selectDatabaseStats
} from '../store/selectors';
import { selectStocks } from '../store/selectors/stockSelectors';

/**
 * Optimized selector hooks that batch multiple state selections
 * to prevent cascade re-renders and improve performance.
 *
 * Note: For individual stock lookups, use the dedicated hooks in
 * useStockSelectors.ts for better performance and stable references.
 */

const selectStocksAndScreening = createSelector(
  [
    selectStocks,
    (state: RootState) => state.stocks.loading,
    (state: RootState) => state.stocks.sortConfig,
    selectScreeningStatus,
    (state: RootState) => state.screening.results,
    selectScreeningProgress,
  ],
  (stocks, loading, sortConfig, screeningStatus, screeningResults, screeningProgress) => ({
    stocks,
    loading,
    sortConfig,
    screeningStatus,
    screeningResults,
    screeningProgress,
  })
);

const selectAnalysisAndFavorites = createSelector(
  [
    (state: RootState) => state.analysisPanel,
    selectFavorites,
    (state: RootState) => state.favorites.loading,
  ],
  (analysisPanel, favorites, favoritesLoading) => ({
    analysisPanel,
    favorites,
    favoritesLoading,
  })
);

const selectChartAndAI = createSelector(
  [
    selectChartData,
    (state: RootState) => state.chart.loading,
    (state: RootState) => state.chart.ticker,
    selectAIAnalysis,
  ],
  (chartData, chartLoading, chartTicker, aiAnalysis) => ({
    chartData,
    chartLoading,
    chartTicker,
    aiAnalysis,
  })
);

const selectGlobalAppState = createSelector(
  [selectGlobalMessage, selectDatabaseStats],
  (globalMessage, databaseStats) => ({
    globalMessage,
    databaseStats,
  })
);

// Combined stocks and screening state selector
export const useStocksAndScreening = () => {
  return useSelector(selectStocksAndScreening);
};

// Combined analysis panel and favorites state selector
export const useAnalysisAndFavorites = () => {
  return useSelector(selectAnalysisAndFavorites);
};

// Combined chart and AI analysis state selector
export const useChartAndAI = () => {
  return useSelector(selectChartAndAI);
};

// Global app state selector
export const useGlobalAppState = () => {
  return useSelector(selectGlobalAppState);
};

// Optimized stock data with derived calculations - stable reference
export const useMemoizedStockData = (stocks: any[]) => {
  return useMemo(() => {
    if (!stocks || stocks.length === 0) {
      return {
        stocks: [],
        highQualityCount: 0,
        strongMomentumCount: 0,
        averageScore: 0,
        passRate: 0,
        sectorBreakdown: {},
        patternBreakdown: {}
      };
    }

    const highQualityCount = stocks.filter(stock => (stock.composite_score || 0) > 700).length;
    const strongMomentumCount = stocks.filter(stock =>
      (stock.volume_surge || 0) > 1.5 &&
      stock.current_price > Math.max(stock.ma_50 || 0, stock.ma_150 || 0, stock.ma_200 || 0)
    ).length;

    const totalScore = stocks.reduce((sum, stock) => sum + (stock.composite_score || 0), 0);
    const averageScore = stocks.length > 0 ? Math.round(totalScore / stocks.length) : 0;
    const passingStocks = stocks.filter(stock => stock.passing_filters).length;
    const passRate = stocks.length > 0 ? Math.round((passingStocks / stocks.length) * 100) : 0;

    // Sector breakdown
    const sectorBreakdown = stocks.reduce((acc, stock) => {
      const sector = stock.sector || 'Unknown';
      acc[sector] = (acc[sector] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Pattern breakdown
    const patternBreakdown = stocks.reduce((acc, stock) => {
      const pattern = stock.pattern_match || 'Unknown';
      acc[pattern] = (acc[pattern] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      stocks,
      highQualityCount,
      strongMomentumCount,
      averageScore,
      passRate,
      sectorBreakdown,
      patternBreakdown
    };
  }, [stocks]);
};

// Optimized favorites with stable sorting
export const useMemoizedFavorites = (favorites: any[], sortConfig: any) => {
  return useMemo(() => {
    if (!favorites || favorites.length === 0) return [];

    if (!sortConfig?.column) return favorites;

    const collator = new Intl.Collator('en', {
      numeric: true,
      sensitivity: 'base',
      caseFirst: 'lower'
    });

    const sorted = [...favorites].sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const result = collator.compare(aVal, bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      return 0;
    });

    return sorted;
  }, [favorites, sortConfig]);
};

// Enhanced number formatter with more utilities
export const useNumberFormatter = () => {
  return useMemo(() => ({
    formatNumber: (num: number) => {
      if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
      if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
      if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
      return num.toString();
    },
    formatPrice: (price: number) => {
      return price.toFixed(2);
    },
    formatPercentage: (percent: number) => {
      return `${percent.toFixed(2)}%`;
    },
    formatCurrency: (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },
    formatMarketCap: (marketCap: number) => {
      // Tests and UI expect "B" for billions (even when values exceed 1e12),
      // and include a leading currency symbol.
      if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
      if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
      return `$${marketCap.toFixed(0)}`;
    }
  }), []);
};
